"use client"
import React, {
  startTransition,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import * as styles from "./search.module.scss"
import { FiltersType, HotelType, SearchPropsType } from "./types"
import {
  GatsbyLink,
  HotelsRatesType,
  Icon,
  isSubset,
  Link,
  localizedAddress,
  parseQuery,
  pricePerNight,
  search,
  Select,
  sortHotels,
  Spinner,
  Title,
  tripadvisorRatingURL,
  useViewport,
} from "@tfehotels/tfe-gatsby-library"
import ImagesCarousel from "../images_carousel"
import { Trans, useTranslation } from "react-i18next"
import { ItemType } from "../../../typeahead/types"
import { useCustomEventListener } from "react-custom-events"
import { meetsMinTripadvisorRating } from "../../../../utils/hotel"
import { AvailabilityDataType, HotelAvailabilityType } from "../rooms/types"
import { SearchParamsType } from "../../../calendar/types"

const SearchWidget = React.lazy(() => import("../search_widget"))
const Map = React.lazy(() => import("../../../map"))
const Filters = React.lazy(() => import("../../../filters"))
const DEFAULT_LIMIT = 10

export default function Search({ data, ...props }: SearchPropsType) {
  const { t } = useTranslation()
  const [top, setTop] = useState(0)
  const isFirstRender = useRef(true)
  const viewport = useViewport()
  const { search_term, search_id, search_index } = parseQuery(
    props.location.search,
  )
  const [hotelsRates, setHotelsRates] = useState<HotelsRatesType>({})
  const [displayMobileMap, setDisplayMobileMap] = useState(false)
  const [spinner, setSpinner] = useState(true)
  const [ratesSpinner, setRatesSpinner] = useState(true)
  const [rawHotels, setRawHotels] = useState<HotelType[]>([])
  const [selectedMarker, setSelectedMarker] = useState<number | null>(null)
  const [filters, setFilters] = useState<FiltersType>({})
  const [showFilters, setShowFilters] = useState<boolean>(false)
  const [limit, setLimit] = useState(DEFAULT_LIMIT)
  const sortingOptions = {
    PRICE_ASC: t("Price (Low - High)"),
    PRICE_DESC: t("Price (High - Low)"),
    RECOMMENDED: t("Recommended"),
  }
  const [sorting, setSorting] =
    useState<keyof typeof sortingOptions>("RECOMMENDED")
  // Keep a ref to the latest AbortController so we can cancel in-flight requests.
  const abortRef = useRef<AbortController | null>(null)

  const hotels = useMemo(() => {
    const activeFilters = new Set(
      Object.entries(filters)
        .filter(([_, v]) => v)
        .map(([k]) => k),
    )
    const filtered = rawHotels.slice(0, limit).map(h => ({
      ...h,
      display: !activeFilters.size || isSubset(activeFilters, h.amenities),
    }))
    return sortHotels(
      filtered as any,
      hotelsRates,
      !!Object.keys(hotelsRates).length && sorting !== "RECOMMENDED",
      sorting === "PRICE_DESC" ? "desc" : "asc",
    ) as any[]
  }, [rawHotels, hotelsRates, filters, sorting, limit])

  useCustomEventListener(
    "sticky-menu-changed",
    (data: { isSticky: boolean; height: number }) => {
      setTop(data.isSticky ? data.height : 0)
    },
  )

  const mapMarkers = useMemo(() => {
    return hotels
      .filter(h => h.display)
      .map(h => ({
        position: h.location,
        title: h.name as string,
        className: hotelsRates[h.id] ? "price-tag" : undefined,
        scrollTo: `hotel-${h.id}`,
        textContent: hotelsRates[h.id]
          ? `${hotelsRates[h.id].currency} ${Math.trunc(Number(hotelsRates[h.id].rate))}`
          : undefined,
        iconUrl: hotelsRates[h.id] ? undefined : "/icons/GLO/map-marker2.svg",
        iconStyles: hotelsRates[h.id] ? undefined : "width:45px; height:45px;",
        description: `<div class="info-window">
                        <div class="hotel-info">
                            <div class="tripadvisor">
                              <div class="flex-aligned" width="119">${
                                meetsMinTripadvisorRating(h.tripadvisor_rating)
                                  ? `<img width="119" height="20" src="${tripadvisorRatingURL(h.tripadvisor_rating)}" loading="lazy" />${
                                      h.tripadvisor_reviews
                                        ? `<span>${t("xReviews", { count: h.tripadvisor_reviews })}</span>`
                                        : ""
                                    }`
                                  : ""
                              }</div>
                            </div>
                            <div class="rate">
                                ${
                                  !!hotelsRates[h.id]
                                    ? `
                                    <div>
                                      ${t("From")}
                                    </div>
                                    <div>
                                      <strong>
                                        ${hotelsRates[h.id]?.rate ?? ""}
                                      </strong>
                                      ${t("currency/night", {
                                        currency:
                                          hotelsRates[h.id]?.currency ?? "AUD",
                                      })}
                                    </div>`
                                    : ""
                                }
                              </div>
                            <a class="btn btn-primary"  target="_blank" rel="noopener noreferrer"' href="${`${h.slug}${props.location.search}`}">
                                  ${t("View Hotel")}
                            </a>
                        </div>
                    </div>`,
      }))
  }, [hotels, hotelsRates, t, props.i18n.language, props.location.search])

  const isLocked = useRef(false)
  const observer = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      // 1. Limpieza: Si el nodo desaparece o cambia, desconectamos el anterior
      observer.current?.disconnect()
      if (node) {
        observer.current = new IntersectionObserver(
          ([entry]) => {
            // 3. Ejecutar solo cuando entra en el viewport y no hay un bloqueo (isLocked)
            if (entry.isIntersecting && !isLocked.current) {
              isLocked.current = true
              setTimeout(() => {
                isLocked.current = false
              }, 3000) // Throttle

              setTimeout(() => {
                setLimit(prev => prev + DEFAULT_LIMIT)
              }, 1000) // Visual delay
            }
          },
          { threshold: 0.5 },
        )

        observer.current.observe(node)
      }
    },
    [setLimit],
  )
  const markersWithRates = useMemo(() => {
    if (Object.keys(hotelsRates).length === 0) return true // Si no hay tarifas, los marcadores están "listos" (vacíos)
    return mapMarkers.some(m => m.textContent !== undefined) // ¿Hay algún marcador con precio?
  }, [mapMarkers, hotelsRates])

  const showLoadMore = useMemo(() => {
    return !ratesSpinner && rawHotels?.length > hotels?.length
  }, [ratesSpinner, rawHotels?.length, hotels?.length])

  useCustomEventListener(
    "render-availability",
    (data: AvailabilityDataType) => {
      startTransition(() => {
        if (data.hotels?.length) {
          let newRates = { ...hotelsRates }
          Object.values(data.hotels).map(
            (HotelAvailability: HotelAvailabilityType) => {
              if (HotelAvailability.rates?.length) {
                const firstKey = Object.keys(HotelAvailability.room_rates)[0]
                newRates[HotelAvailability.code] = {
                  name: HotelAvailability.room_rates[firstKey][0].rate_name,
                  rate: pricePerNight(
                    HotelAvailability.cheapest_rate_total,
                    HotelAvailability.nights,
                  ),
                  currency: HotelAvailability.room_rates[firstKey][0].currency,
                }
              }
            },
          )
          setHotelsRates(newRates)
        }
        setRatesSpinner(false)
      })
    },
  )
  // Listen to the loading-availability to display spinners
  const handleBeforeSearch = async (data: {
    _: any
    manualSearch: boolean
  }) => {
    startTransition(() => {
      setRatesSpinner(true)
      // On manual search (When clicking search button) reset rates and limits
      if (data.manualSearch) {
        setLimit(DEFAULT_LIMIT)
        setHotelsRates({})
      }
    })
  }

  useEffect(() => {
    if (!spinner) {
      startTransition(() => {
        setSpinner(true)
        if (!ratesSpinner) {
          setRatesSpinner(true)
        }
      })
    }
    // Cancel previous request (if any) before starting a new one
    if (abortRef.current) abortRef.current.abort()
    const controller = new AbortController()
    abortRef.current = controller
    if (search_id && search_index) {
      ;(async () => {
        let data: ItemType[] = []
        try {
          data = await search(
            controller,
            search_term,
            search_index,
            search_id,
            [
              "id",
              "name",
              "location",
              "tripadvisor_reviews",
              "tripadvisor_rating",
              "slug",
              "address",
              "city",
              "city_id",
              "state_id",
              "country_id",
              "country",
              "postal_code",
              "recommended_order",
              "images",
              "amenities",
              "tags",
              "project",
            ],
            props.i18n.language,
            100, // Fetch up to 100 hotels
            true, // Fetch hotels
            ["ADI", "ABA"],
          )
          startTransition(() => {
            let filtersSet = new Set<string>()
            setRawHotels(
              data
                .map(h => {
                  let amenities = new Set(h._source.amenities ?? [])
                  for (const a of amenities) {
                    filtersSet.add(a)
                  }

                  return {
                    id: h._source.id,
                    display: true,
                    name: h._source.name,
                    slug:
                      h._source.project === "ABA"
                        ? `${process.env.ABA_URL}/${props.i18n.language}${h._source.slug}`
                        : `/${props.i18n.language}${h._source.slug}${props.location.search}`,
                    tripadvisor_reviews: h._source.tripadvisor_reviews,
                    tripadvisor_rating: h._source.tripadvisor_rating,
                    location: h._source.location,
                    address: h._source.address,
                    city: h._source.city,
                    city_id: h._source.city_id,
                    state_id: h._source.state_id,
                    country: h._source.country,
                    country_id: h._source.country_id,
                    postal_code: h._source.postal_code,
                    recommended_order: h._source.recommended_order,
                    images: h._source.images,
                    amenities: amenities,
                    tags: h._source.tags,
                  } as HotelType
                })
                .sort(
                  (a, b) =>
                    (a.recommended_order ?? Infinity) -
                    (b.recommended_order ?? Infinity),
                ),
            )
            setFilters(
              Object.fromEntries(
                [...filtersSet].sort().map(key => [key, false]), //Sort filters alphabetically
              ),
            )
          })
        } catch (e: any) {
          // Ignore abort errors, surface others
          if (e.name !== "AbortError") console.error(e)
        }
        // Only clear loading if this request is still the current one
        if (abortRef.current === controller) setSpinner(false)
      })()
    } else {
      setSpinner(false)
    }

    if (isFirstRender.current) {
      isFirstRender.current = false
    }

    return () => {
      controller.abort()
    }
  }, [search_term, search_index, search_id])

  // Disable scroll on mobile when filters are displayed
  useEffect(() => {
    if (!isFirstRender.current && viewport === "m") {
      document.body.classList[showFilters ? "add" : "remove"]("no-scroll")
    }
    return () => {
      document.body.classList.remove("no-scroll")
    }
  }, [showFilters, viewport])

  const activeFilters: string[] = Object.entries(filters)
    .filter(([_, v]) => v)
    .map(([k, _]) => k)

  // Filter hotel_codes to load rates in batches of 10 when the limit increases, to avoid long loading times and too many requests at once.
  const handleCustomSearch = (params: SearchParamsType) => {
    const start = limit > DEFAULT_LIMIT ? limit - DEFAULT_LIMIT : 0
    params.hotel_codes = rawHotels.slice(start, limit).map(h => h.id)
    return params
  }

  return (
    <section className={`${styles.section} ${props.className ?? ""}`}>
      {!!data.search_widget && (
        <Suspense>
          <SearchWidget
            key={`searchwidget${props.pk}-${search_id}-${search_index}-${hotels.length}`}
            className="sw-search"
            showSearchInput={true}
            pageParams={props.pageParams}
            location={props.location}
            i18n={props.i18n}
            hidePromocode={true}
            beforeSearch={handleBeforeSearch}
            customSearch={handleCustomSearch}
            {...data.search_widget}
            data={{
              ...data.search_widget.data,
              hotel_codes: hotels.map(h => h.id),
            }}
          />
        </Suspense>
      )}
      <div className="results">
        <div className="col1">
          {spinner && <Spinner className="main-spinner" />}
          {!spinner && (
            <div className="col1-inner">
              <div className="controls">
                <span className="flex-aligned">
                  {!!hotels.length
                    ? t("xHotelsFound", {
                        count: Math.min(limit, hotels.length),
                        location: `${search_index !== "country" ? `${hotels[0].city}, ` : ""}${hotels[0].country}`,
                      })
                    : t("NoHotelsFound")}
                  {displayMobileMap
                    ? !!hotels.length && (
                        <button
                          className="btn btn-alternate flex-aligned map-btn black"
                          onClick={() => setDisplayMobileMap(false)}
                        >
                          <Icon
                            width={20}
                            height={20}
                            icon={{ icon: "x-mark", group: "ADI" }}
                          />
                          <Trans i18nKey={"Close"} />
                        </button>
                      )
                    : !!hotels.length && (
                        <button
                          className="btn btn-alternate flex-aligned map-btn"
                          onClick={() => setDisplayMobileMap(true)}
                        >
                          <Icon
                            width={20}
                            height={20}
                            icon={{ icon: "map", group: "ADI" }}
                          />
                          <Trans i18nKey={"Map"} />
                        </button>
                      )}
                </span>
                {!!hotels.length ? (
                  <div className="flex-aligned">
                    <Select
                      className="select1"
                      name="sort"
                      options={sortingOptions}
                      value={{
                        label:
                          sortingOptions[
                            sorting as keyof typeof sortingOptions
                          ],
                        value: sorting,
                      }}
                      onChange={(option: any, _) => {
                        startTransition(() => {
                          setSorting(option?.value)
                        })
                      }}
                    />
                    <Suspense>
                      <Filters
                        id={`Filters${props.pk}`}
                        filters={filters}
                        showFilters={showFilters}
                        setFilters={setFilters}
                        setShowFilters={setShowFilters}
                        categories={{
                          Amenities: Object.keys(filters).reduce(
                            (acc, key) => {
                              acc[key] = key
                              return acc
                            },
                            {} as { [k: string]: string },
                          ),
                        }}
                      />
                    </Suspense>
                  </div>
                ) : (
                  <GatsbyLink
                    className="btn btn-primary"
                    to={`/${props.i18n.language}/destinations/`}
                  >
                    <Trans i18nKey={"Destinations"} />
                  </GatsbyLink>
                )}
              </div>
              {!!activeFilters.length && (
                <ul className="active-filters">
                  {activeFilters.map((filter, k) => (
                    <li key={`active-filters-${props.pk}-${k} `}>
                      <button
                        className="font-s flex-aligned"
                        onClick={() =>
                          setFilters({
                            ...filters,
                            [filter]: !filters[filter],
                          })
                        }
                      >
                        {filter}{" "}
                        <Icon
                          width={16}
                          height={16}
                          icon={{ icon: "close", group: "ADI" }}
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <ul
                className={`hotel-list${displayMobileMap ? " hidden_mobile" : ""}`}
              >
                {hotels.map(
                  (hotel, k) =>
                    !!hotel.display && (
                      <li
                        id={`hotel-${hotel.id}`}
                        key={`search-hotel${k}-${hotel.id}-${+ratesSpinner}`}
                      >
                        <div className="images">
                          <ImagesCarousel
                            id={`search${props.pk}-carousel${hotel.id}`}
                            images={hotel.images}
                            showDots={true}
                          />
                          <div className="ratting font-xs flex-aligned">
                            {meetsMinTripadvisorRating(
                              hotel.tripadvisor_rating,
                            ) && (
                              <>
                                <img
                                  width="119"
                                  height="20"
                                  src={tripadvisorRatingURL(
                                    hotel.tripadvisor_rating,
                                  )}
                                  loading="lazy"
                                />
                                {!!hotel.tripadvisor_reviews && (
                                  <span>
                                    {t("xReviews", {
                                      count: hotel.tripadvisor_reviews,
                                    })}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="details">
                          <div className="info">
                            {!!Object.keys(hotel.tags).length && (
                              <div className="property-tags">
                                {Object.values(hotel.tags).map((tag, k) => (
                                  <div
                                    key={`hoteltag-${hotel.id}-${k}`}
                                    className="flex-aligned"
                                  >
                                    <Icon
                                      width={16}
                                      height={16}
                                      icon={{ icon: "red-arrow", group: "ADI" }}
                                    />
                                    {tag as string}
                                  </div>
                                ))}
                              </div>
                            )}
                            <Link className="link4" external_link={hotel.slug}>
                              <Title
                                className="h5"
                                title={`### ${hotel.name}`}
                              />
                            </Link>
                            <span className="font-s iphone-fix">
                              <Icon
                                width={16}
                                height={16}
                                icon={{ icon: "location", group: "ADI" }}
                              />
                              {localizedAddress(hotel.country_id, {
                                address1: hotel.address,
                                city: hotel.city,
                                stateId: hotel.state_id,
                                postalCode: hotel.postal_code,
                              })}
                            </span>
                          </div>
                          <div className="rate">
                            {ratesSpinner ? (
                              <Spinner />
                            ) : (
                              !!hotelsRates[hotel.id] && (
                                <>
                                  <div>
                                    <Trans i18nKey="From" />
                                  </div>
                                  <div>
                                    <strong>
                                      {hotelsRates[hotel.id]?.rate ?? ""}
                                    </strong>{" "}
                                    {hotelsRates[hotel.id]?.currency ?? "AUD"}
                                    /<Trans i18nKey="night" />
                                  </div>
                                </>
                              )
                            )}
                          </div>
                          <Link
                            className="btn btn-primary"
                            external_link={hotel.slug}
                          >
                            <Trans i18nKey={"View Hotel"} />
                          </Link>
                        </div>
                      </li>
                    ),
                )}
              </ul>
              {showLoadMore && !displayMobileMap && (
                <div ref={loadMoreRef}>
                  <Spinner />
                </div>
              )}
            </div>
          )}
        </div>
        <div className={`col2${displayMobileMap ? "" : " hidden_mobile"}`}>
          <div
            className="map-wrapper"
            style={{
              top: `${top}px`,
            }}
          >
            {spinner || (!!hotels.length && ratesSpinner) ? (
              <Spinner />
            ) : (
              !!hotels.length && (
                <Suspense>
                  <Map
                    key={`map-${hotels[0]?.id ?? "empty"}-${Object.keys(hotelsRates).length}-${markersWithRates}`} // Force remount when hotels or rates change to ensure info windows are updated
                    className="map"
                    center={hotels[0].location}
                    zoom={12}
                    selectedMarker={selectedMarker}
                    categories={[]}
                    handleSelectMarker={(k: number) =>
                      startTransition(() => setSelectedMarker(k))
                    }
                    markers={mapMarkers}
                  />
                  {showLoadMore && (
                    <button
                      className="btn btn-primary map-load-more"
                      onClick={() => setLimit(limit + DEFAULT_LIMIT)}
                    >
                      {t("More hotels")}
                    </button>
                  )}
                </Suspense>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
